import { useReducer, useState } from 'react';
import { useCreate, useGo } from '@refinedev/core';
import { Library } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FormHeader, FormStickyActions } from '@/components/forms';
import { notify } from '@/lib/notify';
import {
  BookBasicForm,
  bookBasicReducer,
  buildBookFormData,
  initBookState,
  toBookSlug,
} from './components/BookBasicForm';
import type { Book, BookFormData, BookTranslationData } from './types';

/**
 * Создание книги. Только базовые поля + минимальный перевод (RU).
 * Остальное (переводы, главы) редактируется в edit view после создания.
 */
export function BooksCreate() {
  const go = useGo();
  const [state, dispatch] = useReducer(bookBasicReducer, undefined, () => initBookState());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutate, mutation: createMutation } = useCreate<Book>();
  const isPending = createMutation.isPending;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!state.ruTitle.trim()) {
      errs.ruTitle = 'Название обязательно';
    }
    if (!state.slug.trim()) {
      errs.slug = 'Slug обязателен';
    }
    if (state.format !== 'AUDIO' && state.sourceFile === null) {
      errs.sourceFile = 'Загрузите исходный файл';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) { return; }

    const translations: BookTranslationData[] = [
      {
        languageCode: 'ru',
        title: state.ruTitle.trim(),
        description: null,
        authorDisplay: state.author.trim() || null,
      },
    ];

    try {
      const data: BookFormData = buildBookFormData(
        { ...state, slug: toBookSlug(state.slug) },
        translations,
      );

      mutate(
        { resource: 'books', values: data },
        {
          onSuccess: (response) => {
            const id = response.data.id;
            go({ to: `/books/edit/${id}` });
          },
          onError: (err) => {
            notify.error(err instanceof Error ? err.message : 'Ошибка создания');
          },
        },
      );
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Ошибка валидации');
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <FormHeader
        title="Новая книга"
        description="Создайте книгу — главы и переводы можно будет добавить после создания"
        sticky
        icon={<Library className="h-5 w-5" />}
      />

      <BookBasicForm
        state={state}
        dispatch={dispatch}
        isEdit={false}
        errors={errors}
      />

      <FormStickyActions>
        <Button variant="outline" onClick={() => go({ to: '/books' })}>
          Отмена
        </Button>
        <Button onClick={handleSubmit} disabled={isPending}>
          {isPending ? 'Создание...' : 'Создать книгу'}
        </Button>
      </FormStickyActions>
    </div>
  );
}
